import RoundedRect from 'Phaser/GameObjects/RoundedRect';
import Phaser from 'phaser';
import { colors } from 'Phaser/config';
import { NotifyPhaserEvents, NotifyReactEvents, RemoveListener } from 'Phaser/EventBus2';
import { MidiEventState, PrerenderedGraphics, PrerenderedPhraseData } from 'Types';
import EventStream, { NoteEvent } from 'Models/EventStream';
import { INoteGraphicsDatabase } from 'Phaser/Scenes/StaffExercise';
import Phrase, { Iterator } from 'Models/Phrase';
import { NoteType } from '@museflow-ai/opensheetmusicdisplay';
import { ProgressState } from './Progress';
import TimeSignature from 'Models/TimeSignature';
import ITimeKeeper from 'Models/ITimeKeeper';
import { COLORBLIND_TYPE } from 'Utils/Constants';
import { ErrorFeedbackManager } from './ErrorFeedbackManager';
import SightReadingPhaser from 'Phaser/Scenes/SightReadingPhaser';
import { NoteGraphic } from './StaffLine/Note';
import { FeatureFlag, isFeatureEnabled } from 'Utils/FeatureFlags';

export const getPhraseDuration = (cursorData: PrerenderedPhraseData[]): number => {
  if (cursorData.length === 0) {
    return 0;
  }
  let last: PrerenderedPhraseData = cursorData[0];
  for (const slice of cursorData) {
    if (last === null || slice.currentTimestamp > last.currentTimestamp) {
      last = slice;
    }
  }
  const lastOffset = Math.max(
    ...last!.currentVoiceEntries.map(({ Notes }) => Math.max(...Notes.map(({ length }) => length))),
  );
  return last!.currentTimestamp + lastOffset;
};

type PhraseMeasure = {
  startMeasureIndex: number;
  endIndex: number;
};

/**
 * Interface for iterators that can be used with CursorMobile.
 * Both IteratorExtensible and OSMDIteratorExtensible implement this interface.
 */
export interface ICursorIterator {
  measureTimestamps: number[];
  lastPlayedMeasure: number;
  timestampOffset: number;
  currentTimeStamp: number;
  currentMeasureIndex: number;
  cursorData: PrerenderedPhraseData[];
  //currentMeasureIndexRelativeToPhrase: number;
  //phraseMeasures: PhraseMeasure[];

  // Accessor properties (getters)
  readonly CurrentMeasureIndex: number;
  readonly EndReached: boolean;
  readonly CurrentVoiceEntries: PrerenderedPhraseData['currentVoiceEntries'];

  // Methods
  left(_dbg?: boolean): number;
  getNextTimestamp(): number;
  totalDuration(): number;
  currentPhraseEndTimestamp(): number;
  lastNoteDuration(): number;
  next(
    tempo?: number,
    onLastSlice?: (start: number, duration: number, durationInSecs: number) => void,
    ctx?: unknown,
    onStartPlayingMeasure?: (globalMeasure: number, phraseLocalMeasure: number) => void,
    onStartPlayingMeasureContext?: unknown,
  ): void;
  addPhrase(
    cursorData: PrerenderedPhraseData[],
    timeSig: TimeSignature,
    phraseId: string,
    phraseIndex: number,
  ): void;
  rollbackToMeasure(measure: number, timestamp?: number): void;
  checkPhraseCompletion(
    timestamp: number,
    eligible: boolean,
    onFinishPlaying?: (eligible?: boolean) => void,
    cbCtx?: object,
    idx?: number,
  ): void;
}

type MeasureMetaData = {
  // Index of the first vertical slice in the measure
  startIndex: number;
  // Index of the phrase that the measure belongs to
  phraseIndex: number;
};

type PhraseMetadata = {
  startMeasureIndex: number;
  lastSliceIndex: number;
  endTimestamp: number;
  wasFullyPlayed: boolean;
};

// This will not work when used outside a phaser context
export class IteratorExtensible extends Iterator implements ICursorIterator {
  totalLength: number;
  scene: INoteGraphicsDatabase;
  measureInfo: MeasureMetaData[];
  lastPlayedMeasure: number = 0;
  timestampOffset: number = 0;
  measureTimestamps: number[] = [];
  shouldIncrementMeasureIndex: boolean = false;
  shouldIncrementPhraseIndex: boolean = false;
  currentMeasureBeingPlayed: number = -1;
  phraseInfo: PhraseMetadata[] = [];
  phraseIndex: number = 0;
  lastPlayedPhraseIndex: number = 0;
  totalMeasures: number = 0;
  totalSlices: number = 0;
  flag = false;

  constructor(
    scene: INoteGraphicsDatabase,
    cursorData: PrerenderedPhraseData[],
    phrase: Phrase,
    timeSig: TimeSignature,
    timestampOffset?: number,
    _mobile?: boolean,
    phraseIndex: number = 0,
  ) {
    super(cursorData, phrase);
    this.phraseIndex = phraseIndex;
    this.measureInfo = [{ startIndex: 0, phraseIndex: phraseIndex }];
    if (this.cursorData.length > 0) {
      this.phraseInfo.push({
        startMeasureIndex: 0,
        lastSliceIndex: this.cursorData.length - 1,
        endTimestamp: getPhraseDuration(cursorData),
        wasFullyPlayed: false,
        //lastCumulativeMeasure: this.cursorData[this.cursorData.length - 1].currentMeasureIndex + 1,
      });
      this.measureTimestamps = this.cursorData.map(
        (_, idx) => (1 / timeSig.denominator) * timeSig.numerator * idx,
      );
      this.totalSlices = cursorData.length;
      this.totalMeasures = cursorData.last().currentMeasureIndex + 1;
    }
    this.scene = scene;
    this.totalLength = getPhraseDuration(cursorData);
    this.timestampOffset = timestampOffset ?? 0;
  }

  addPhrase(
    cursorData: PrerenderedPhraseData[],
    timeSig: TimeSignature,
    _phraseId: string,
    phraseIndex: number,
  ) {
    this.phraseInfo[phraseIndex] = {
      startMeasureIndex: this.totalMeasures,
      lastSliceIndex: this.totalSlices + cursorData.length - 1,
      endTimestamp: this.totalLength + getPhraseDuration(cursorData),
      wasFullyPlayed: false,
      /*lastCumulativeMeasure:
        (this.phraseInfo[phraseIndex - 1]?.lastCumulativeMeasure ?? 0) +
        (cursorData[cursorData.length - 1].currentMeasureIndex + 1),*/
    };
    this.cursorData.push(
      ...cursorData
        .map((item) => ({
          ...item,
          currentVoiceEntries: item.currentVoiceEntries.filter(
            (entry) => entry.Notes.filter((note) => note.printObject).length > 0,
          ),
          currentTimestamp: item.currentTimestamp + this.totalLength,
        }))
        .filter(({ currentVoiceEntries }) => currentVoiceEntries.length > 0),
    );
    const lastMeasure = this.measureTimestamps.length;
    this.measureTimestamps.push(
      ...cursorData.map(
        (_, idx) => (1 / timeSig.denominator) * timeSig.numerator * (idx + lastMeasure),
      ),
    );
    this.totalLength += getPhraseDuration(cursorData);
    this.endReached = false;
    this.totalMeasures += cursorData.last().currentMeasureIndex + 1;
    this.totalSlices += cursorData.length;
  }

  lastNoteDuration() {
    return this.totalLength - this.cursorData[this.cursorData.length - 1].currentTimestamp;
  }

  getPhraseEndPosition(): number | undefined {
    return this.scene.getPhraseEndPosition();
  }

  getGraphicsLeft(currentNoteId: string, rest?: boolean): number | undefined {
    // getNotePosition may return a number or { x, y } depending on the scene
    const pos = this.scene.getNotePosition(
      currentNoteId,
      this.currentTimeStamp + this.timestampOffset,
      rest,
    ) as { x: number; y: number } | number | null;
    if (pos === null || pos === undefined) return undefined;
    return typeof pos === 'number' ? pos : pos.x;
  }

  totalDuration() {
    return this.totalLength;
  }

  currentPhraseEndTimestamp(): number {
    return this.phraseInfo[this.phraseIndex]?.endTimestamp ?? this.totalLength;
  }

  getLastPlayedMeasure(): number {
    return this.lastPlayedMeasure;
  }

  left(_dbg?: boolean): number {
    try {
      if (this.EndReached) {
        return this.getPhraseEndPosition()!;
      } else {
        // Track each (id, position) lookup so we can identify exactly which
        // note id is missing from the scene when positions go non-finite.
        const lookups: { id: string; pos: number | undefined; isRest: boolean }[] = [];
        for (const entry of this.CurrentVoiceEntries) {
          for (const note of entry.Notes) {
            if (note.isRest) continue;
            const id = note.graphics[0].staveNote.id;
            lookups.push({ id, pos: this.getGraphicsLeft(id), isRest: false });
          }
        }
        let positions: (number | undefined)[] = lookups.map((l) => l.pos);
        if (positions.length === 0) {
          // Fallback: include rests (matches the original behaviour).
          for (const entry of this.CurrentVoiceEntries) {
            for (const note of entry.Notes) {
              if (!note.printObject) continue;
              const id = note.graphics[0].staveNote.id;
              const pos = this.getGraphicsLeft(id, note.isRest);
              lookups.push({ id, pos, isRest: note.isRest });
              positions.push(pos);
            }
          }
        }
        const result = Math.min(...(positions as number[]));
        if (!Number.isFinite(result) && !this.flag) {
          this.flag = true;
          // For each failing lookup, ask the scene whether the id exists at
          // all (no timestamp filter) so we can split "id never existed in any
          // active phrase" from "id exists but timestamp doesn't match the
          // clef's startTimestamp + note.timestamp".
          const sceneAny = this.scene as unknown as {
            phrases?: {
              graphic: {
                _data: { uuid: string; startTimestamp: number };
                trebleClef?: { notes?: Map<string, { timestamp?: number }> };
                bassClef?: { notes?: Map<string, { timestamp?: number }> };
              };
            }[];
          };
          const lookupDetails = lookups.map((l) => {
            // For each active phrase, see if the id is in its treble or bass
            // clef map directly and what timestamp the note carries.
            const found = (sceneAny.phrases ?? [])
              .map((p, slot) => {
                if (!p) return null;
                const treble = p.graphic.trebleClef?.notes?.get(l.id);
                const bass = p.graphic.bassClef?.notes?.get(l.id);
                const note = treble ?? bass;
                if (!note) return null;
                const noteTs = note.timestamp;
                const startTs = p.graphic._data.startTimestamp;
                return {
                  slot,
                  uuid: p.graphic._data.uuid,
                  clef: treble ? 'treble' : 'bass',
                  noteTs,
                  startTs,
                  absTs: (noteTs ?? Infinity) + startTs,
                };
              })
              .filter((x): x is NonNullable<typeof x> => x !== null);
            return { ...l, foundIn: found };
          });
          const activePhrases = (sceneAny.phrases ?? []).map((p, i) =>
            p
              ? { slot: i, uuid: p.graphic._data.uuid, startTs: p.graphic._data.startTimestamp }
              : { slot: i, empty: true },
          );
          console.warn('[IteratorExtensible.left] non-finite position', {
            result,
            indx: this.indx,
            phraseIndex: this.phraseIndex,
            currentTimeStamp: this.currentTimeStamp,
            timestampOffset: this.timestampOffset,
            lookupTimestamp: this.currentTimeStamp + this.timestampOffset,
            cursorDataLength: this.cursorData.length,
            lookups: lookupDetails,
            phraseInfo: this.phraseInfo,
            activePhrases,
          });
        }
        return result;
      }
    } catch (error) {
      console.error('Error when getting the x-position of the next note to play.', error);
      return 0;
    }
  }

  getNextTimestamp(): number {
    if (this.EndReached) {
      return this.totalLength;
    } else return this.currentTimeStamp;
  }

  next(
    tempo?: number,
    onLastSlice?: (start: number, duration: number, durationInSecs: number) => void,
    ctx?: unknown,
    onStartPlayingMeasure?: (globalMeasure: number, phraseLocalMeasure: number) => void,
    onStartPlayingMeasureContext?: unknown,
  ) {
    console.debug('[IteratorExtensible.next]', {
      currentMeasureIndex: this.currentMeasureIndex,
      lastPlayedMeasure: this.lastPlayedMeasure,
      shouldIncrementMeasureIndex: this.shouldIncrementMeasureIndex,
      shouldIncrementPhraseIndex: this.shouldIncrementPhraseIndex,
      phraseIndex: this.phraseIndex,
      indx: this.indx,
      cursorDataLength: this.cursorData.length,
      phraseInfo: this.phraseInfo,
    });
    if (this.indx >= this.cursorData.length) {
      console.log('length:', this.cursorData.length, this.indx);
    }
    this.indx += 1;
    if (this.indx >= this.cursorData.length) {
      this.endReached = true;
    } else {
      this.currentMeasureIndex =
        this.cursorData[this.indx].currentMeasureIndex +
        (this.phraseInfo[this.phraseIndex]?.startMeasureIndex ?? 0);
      this.currentTimeStamp = this.cursorData[this.indx].currentTimestamp;
    }
    if (this.phraseInfo[this.phraseIndex] === undefined) {
      console.debug('[IteratorExtensible.next] phraseInfo[this.phraseIndex] is undefined', {
        phraseIndex: this.phraseIndex,
        phraseInfo: this.phraseInfo,
      });
    }
    //super.next();
    if (this.currentMeasureBeingPlayed !== this.getCurrentMeasure()) {
      this.currentMeasureBeingPlayed = this.getCurrentMeasure();
      // `currentMeasureIndex` is cumulative (slice.currentMeasureIndex +
      // phrase.startMeasureIndex). `currentMeasureBeingPlayed` is phrase-local
      // because cursorData retains each phrase's own currentMeasureIndex
      // through addPhrase. The PhraseManager uses the phrase-local value to
      // trigger prefetch on measure 2 of each phrase regardless of which
      // phrase the shared mobile cursor is currently in.
      onStartPlayingMeasure?.bind(onStartPlayingMeasureContext)(
        this.currentMeasureIndex,
        this.currentMeasureBeingPlayed,
      );
    }
    if (this.shouldIncrementMeasureIndex) {
      this.lastPlayedMeasure++;
      this.shouldIncrementMeasureIndex = false;
    } else if (this.currentMeasureIndex > this.lastPlayedMeasure) {
      //this.starts[this.currentMeasureIndex] = this.indx;
      this.measureInfo.push({ startIndex: this.indx, phraseIndex: this.phraseIndex });
      this.shouldIncrementMeasureIndex = true;
      console.debug('[IteratorExtensible.next] shouldIncrementPhraseIndex', {
        currentMeasureIndex: this.currentMeasureIndex,
        lastPlayedMeasure: this.lastPlayedMeasure,
        shouldIncrementMeasureIndex: this.shouldIncrementMeasureIndex,
        shouldIncrementPhraseIndex: this.shouldIncrementPhraseIndex,
        phraseIndex: this.phraseIndex,
        indx: this.indx,
        phraseInfo: this.phraseInfo,
      });
    }
    if (this.shouldIncrementPhraseIndex) {
      this.phraseIndex++;
      this.shouldIncrementPhraseIndex = false;
    } else if (this.indx > this.phraseInfo[this.phraseIndex].lastSliceIndex) {
      console.debug('[IteratorExtensible.next] incrementing phrase index');
      this.shouldIncrementPhraseIndex = true;
    }
    //console.log('length', this.indx, this.cursorData.length, this.EndReached);
    if (tempo) {
      const durationInSecs = (this.totalDuration() - this.currentTimeStamp) * (60 / tempo) * 4;
      const isLastSlice = this.endReached || this.shouldIncrementPhraseIndex;
      if (isLastSlice) {
        onLastSlice?.bind(ctx)(
          this.currentTimeStamp,
          this.totalDuration() - this.currentTimeStamp,
          durationInSecs,
        );
      }
    }
  }

  getCurrentMeasure(): number {
    if (this.indx == 0) {
      return 0;
    } else {
      return this.cursorData[this.indx - 1].currentMeasureIndex;
    }
  }

  rollbackToMeasure(measure: number) {
    this.endReached = false;
    this.indx = this.measureInfo[measure].startIndex - 1;
    console.debug('[IteratorExtensible.rollbackToMeasure]', {
      measureInfo: this.measureInfo,
      measure: measure,
      rollbackPhraseIndex: this.measureInfo[measure].phraseIndex,
      phraseIndex: this.phraseIndex,
    });
    this.phraseIndex = this.measureInfo[measure].phraseIndex;
    this.shouldIncrementPhraseIndex = false;
    // Re-arm checkPhraseCompletion for the rollback target and anything past
    // it: the flag is sticky, so without this a replay through an
    // already-finished phrase silently skips onFinishPlaying and the next
    // phrase never activates.
    for (let i = this.phraseIndex; i < this.phraseInfo.length; i++) {
      if (this.phraseInfo[i]) this.phraseInfo[i].wasFullyPlayed = false;
    }
    console.debug('[IteratorExtensible.rollbackToMeasure] new phraseIndex', this.phraseIndex);
    this.next(0);
  }

  checkPhraseCompletion(
    timestamp: number,
    eligible: boolean,
    onFinishPlaying: (eligible?: boolean) => void,
    cbCtx: object,
    idx?: number,
  ) {
    if (this.indx === 0) return;
    const entry = this.phraseInfo[this.phraseIndex];
    if (!entry) return;
    // Fire exactly once per phrase — the cursor sits past the end timestamp
    // for several frames before phraseIndex advances, so we'd otherwise re-fire
    // every tick (causing duplicate phrase requests + scene-side activePhrase
    // walking past the next slot).
    if (timestamp >= entry.endTimestamp && !entry.wasFullyPlayed) {
      entry.wasFullyPlayed = true;
      console.debug('[IteratorExtensible.checkPhraseCompletion] finished playing', {
        idx,
        phraseIndex: this.phraseIndex,
        end: entry.endTimestamp,
      });
      onFinishPlaying?.call(cbCtx, false);
    }
  }
}

export class EventStreamPhaser2 extends EventStream {
  private errorFeedbackManager?: ErrorFeedbackManager;
  private scene?: SightReadingPhaser;

  /**
   * Set the error feedback manager for spawning visual feedback.
   */
  setErrorFeedbackManager(manager: ErrorFeedbackManager, scene: SightReadingPhaser): void {
    this.errorFeedbackManager = manager;
    this.scene = scene;
  }

  protected markOnsetAccuracyWithSideEffects(
    note: NoteEvent,
    eventState: MidiEventState,
    timestamp: number,
    pulse: boolean = false,
    midiNote: number | null,
  ) {
    // Always call super first to ensure base functionality (note coloring, pulses, etc.) works
    super.markOnsetAccuracyWithSideEffects(note, eventState, timestamp, pulse, midiNote);

    // Then handle additional visual feedback for errors
    switch (eventState) {
      case MidiEventState.EARLY_ONSET:
      case MidiEventState.LATE_ONSET:
      case MidiEventState.WRONG_NOTE:
        if (isFeatureEnabled(FeatureFlag.USE_ERROR_FEEDBACK))
          NotifyReactEvents.ShowFeedbackToast({ state: eventState });
        // Spawn visual feedback if manager is available
        if (this.errorFeedbackManager && this.scene) {
          this.spawnOnsetErrorFeedback(note, eventState, midiNote);
        }
        break;
    }
  }

  protected markOffsetAccuracyWithSideEffects(
    note: NoteEvent,
    eventState: MidiEventState,
    timestamp: number | null,
  ) {
    // Always call super first to ensure base functionality (note coloring, etc.) works.
    // Note: super calls makeNoteColor → makeNoteSemiError → SetNoteState(LateStart), which is
    // blocked by the onset-downgrade guard in NoteGraphic.setTimingState when the onset was
    // already correctly played (Ended state).
    super.markOffsetAccuracyWithSideEffects(note, eventState, timestamp);

    // For an offset timing error, the super call's LateStart state gets blocked by the
    // onset-downgrade guard when the onset was already correctly played (Ended). We
    // explicitly emit MistimedRelease instead, which correctly represents that the note
    // was released off-time (too early, or held too long) and is allowed through the guard.
    // Covers both EARLY_OFFSET and LATE_OFFSET; a note-off is exactly one classification,
    // so only one branch fires and it cannot double up with the onset path.
    if (eventState === MidiEventState.EARLY_OFFSET || eventState === MidiEventState.LATE_OFFSET) {
      const colorblindType = (this as unknown as { colorblindType: COLORBLIND_TYPE | null })
        .colorblindType;
      for (const graphic of note.Graphics) {
        NotifyPhaserEvents.SetNoteState({
          id: graphic.staveNote.id,
          state: ProgressState.MistimedRelease,
          colorblindType,
          noteTimestamp: note.Timestamp,
        });
      }
    }

    // Then handle additional visual feedback for errors
    switch (eventState) {
      case MidiEventState.EARLY_OFFSET:
      case MidiEventState.LATE_OFFSET:
        if (isFeatureEnabled(FeatureFlag.USE_ERROR_FEEDBACK))
          NotifyReactEvents.ShowFeedbackToast({ state: eventState });
        // Spawn visual feedback if manager is available
        if (this.errorFeedbackManager && this.scene) {
          this.spawnOffsetErrorFeedback(note, eventState);
        }
        break;
    }
  }

  /**
   * Spawn visual feedback for onset errors (early/late/wrong note).
   */
  private spawnOnsetErrorFeedback(
    note: NoteEvent,
    eventState: MidiEventState,
    midiNote: number | null,
  ): void {
    if (!this.scene || !this.errorFeedbackManager) return;

    const activePhrase = this.scene.phrases[this.scene.getActivePhrase()];
    if (!activePhrase || !activePhrase.cursor) return;

    const cursorWorldX = activePhrase.cursor.x;
    const phraseGraphic = activePhrase.graphic;
    const noteId = note.Graphics[0].staveNote.id;
    const originalNote = phraseGraphic.getNoteById(noteId);

    if (!originalNote) {
      console.warn('Could not find original note for error feedback', noteId);
      return;
    }

    // Get note length from original note
    const noteLength = (originalNote as NoteGraphic).noteLength ?? 0.25;

    // Get colorblind type from EventStream (base class has private colorblindType)
    const colorblindType = (this as unknown as { colorblindType: COLORBLIND_TYPE | null })
      .colorblindType;

    // Track note-on position for offset bar calculation
    const phraseUuid = phraseGraphic._data.uuid;
    this.errorFeedbackManager.setNoteOnPosition(phraseUuid, noteId, cursorWorldX);

    // Spawn phantom note
    const phantomNoteResult = phraseGraphic.spawnPhantomNote(
      cursorWorldX,
      eventState,
      noteId,
      midiNote,
      noteLength,
      colorblindType,
    );

    if (phantomNoteResult) {
      this.errorFeedbackManager.addPhantomNote(phraseUuid, phantomNoteResult, noteId);
    }
  }

  /**
   * Spawn visual feedback for offset errors (early/late release).
   */
  private spawnOffsetErrorFeedback(note: NoteEvent, _eventState: MidiEventState): void {
    if (!this.scene || !this.errorFeedbackManager) return;

    const activePhrase = this.scene.phrases[this.scene.getActivePhrase()];
    if (!activePhrase || !activePhrase.cursor) return;

    const cursorWorldX = activePhrase.cursor.x;
    const phraseGraphic = activePhrase.graphic;
    const noteId = note.Graphics[0].staveNote.id;
    const originalNote = phraseGraphic.getNoteById(noteId);

    if (!originalNote) {
      console.warn('Could not find original note for offset error feedback', noteId);
      return;
    }

    const phraseUuid = phraseGraphic._data.uuid;
    const noteOnX = this.errorFeedbackManager.getNoteOnPosition(phraseUuid, noteId);

    if (noteOnX === undefined) {
      console.warn('Could not find note-on position for offset error feedback', noteId);
      return;
    }

    // Get the phantom note if it exists (for combined errors)
    const _phantomNote = this.errorFeedbackManager.getPhantomNote(phraseUuid, noteId);

    // Determine which clef the note belongs to
    const trebleNote = phraseGraphic.trebleClef?.getNote(noteId);
    const clef: 'treble' | 'bass' = trebleNote ? 'treble' : 'bass';

    // Get note y position (in clef local coordinates)
    // Use the note's line number to calculate y position relative to clef
    const noteLine = (originalNote as NoteGraphic).line;
    const noteY = (noteLine / 2) * phraseGraphic.config.pitchLineGap;

    // Get colorblind type
    const colorblindType = (this as unknown as { colorblindType: COLORBLIND_TYPE | null })
      .colorblindType;

    // Spawn duration bar
    const bar = phraseGraphic.spawnPhantomBar(noteOnX, cursorWorldX, noteY, clef, colorblindType);

    if (bar) {
      this.errorFeedbackManager.addPhantomBar(phraseUuid, bar);
    }
  }

  pulseMatchedNote(note: NoteEvent, _colorClass: string, noteTimestamp?: number) {
    const colorblindType = (this as unknown as { colorblindType: COLORBLIND_TYPE | null })
      .colorblindType;
    NotifyPhaserEvents.PulseNote({
      id: note.Graphics[0].staveNote.id,
      colorblindType,
      noteTimestamp,
    });
  }

  makeNoteSemiError = (
    graphics: PrerenderedGraphics[],
    cbType: COLORBLIND_TYPE | null,
    noteTimestamp?: number,
  ) => {
    console.log('semi error');
    for (const graphic of graphics) {
      NotifyPhaserEvents.SetNoteState({
        id: graphic.staveNote.id,
        state: ProgressState.LateStart,
        colorblindType: cbType,
        noteTimestamp,
      });
    }
  };

  makeNoteCorrect = (
    graphics: PrerenderedGraphics[],
    cbType: COLORBLIND_TYPE | null,
    noteTimestamp?: number,
  ) => {
    console.log('correct');
    for (const graphic of graphics) {
      NotifyPhaserEvents.SetNoteState({
        id: graphic.staveNote.id,
        state: ProgressState.Ended,
        colorblindType: cbType,
        noteTimestamp,
      });
    }
  };

  makeNoteError = (
    graphics: PrerenderedGraphics[],
    cbType: COLORBLIND_TYPE | null,
    noteTimestamp?: number,
  ) => {
    console.log('error');
    for (const graphic of graphics) {
      NotifyPhaserEvents.SetNoteState({
        id: graphic.staveNote.id,
        state: ProgressState.Missed,
        colorblindType: cbType,
        noteTimestamp,
      });
    }
  };
}

const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
const clamp = (a: number, min: number, max: number) => (a < min ? min : a > max ? max : a);

export default class CursorMobile extends RoundedRect {
  private lastExpectedOnset: number = -1;
  private timestampOffset?: number;
  private relPos: number = 0;
  private lastNotePos: number = 0;
  private midiStream: EventStreamPhaser2;
  private tempo: number = 0;
  iterator: ICursorIterator;

  private running: boolean = false;

  // Diagnostic counters
  private _updateCallCount: number = 0;
  private _pausedReturnCount: number = 0;
  private _lastLoggedPos: number = -Infinity;

  private blinkTween?: Phaser.Tweens.Tween;
  private fadeTween?: Phaser.Tweens.Tween;
  private origAlpha: number;

  private eligible: boolean = false;
  private firstPhrase: boolean = false;

  private onLastSlice?: (start: number, duration: number, durationInSecs: number) => void;
  private onFinishPlaying?: (eligible?: boolean) => void;
  private finishCbScope?: object;
  private onStartPlayingMeasure?: (globalMeasure: number, phraseLocalMeasure: number) => void;
  private onStartPlayingMeasureContext?: object;

  idx: number = 0;
  startingMeasure: number = 0;

  flag = true;
  countInDuration?: number;
  enabled = false;
  // The global "Toggle cursor" state, owned by the scene and mirrored onto every
  // cursor (including ones created later). It is ONLY about the user toggle —
  // distinct from gameplay hiding (inactive/just-loaded phrases hide via their
  // own alpha). At draw time it acts as a pure multiplicative mask: the rendered
  // alpha is the cursor's own computed alpha × (visible ? 1 : 0), so it can only
  // force the cursor invisible, never reveal one whose own alpha is already 0.
  private cursorVisible = true;

  timer?: Phaser.Time.TimerEvent;
  lastPlayedMeasure?: number;

  waitForTimerReset: boolean = false;
  playedOnce: boolean = false;

  cursorData: PrerenderedPhraseData[] = [];

  movementTweenHandle?: Phaser.Tweens.Tween;

  /** Demo mode: use these instead of midiStream.timeKeeper and iterator when set */
  private demoTimeKeeper: ITimeKeeper | null = null;
  private demoIterator: ICursorIterator | null = null;
  /** Saved cursor state to restore when clearDemoOverride() is called */
  private savedDemoState: {
    iterator: ICursorIterator;
    lastNotePos: number;
    lastExpectedOnset: number;
    running: boolean;
    timestampOffset: number | undefined;
    relPos: number;
  } | null = null;

  private savedPosition?: number;

  constructor(
    scene: INoteGraphicsDatabase & Phaser.Scene,
    x: number,
    y: number,
    width: number,
    height: number,
    midiStream: EventStreamPhaser2,
    iterator: ICursorIterator,
    cursorData: PrerenderedPhraseData[],
    onLastSlice?: (start: number, duration: number, durationInSecs: number) => void,
    onFinishPlaying?: (eligible?: boolean) => void,
    cbScope?: object,
    hidden?: boolean,
    timestampOffset?: number,
    idx?: number,
    firstPhrase?: boolean,
    onStartPlayingMeasure?: (globalMeasure: number, phraseLocalMeasure: number) => void,
    onStartPlayingMeasureContext?: object,
    cursorColor: number = colors.cursor,
  ) {
    console.debug('[CursorMobile] constructor', { x, y, width, height, hidden });
    super(scene, x, y, width, height, width / 2, cursorColor, colors.cursorAlpha);
    this.cursorData = cursorData;
    this.firstPhrase = firstPhrase ?? false;
    this.idx = idx ?? 0;
    this.lastNotePos = x;
    this.timestampOffset = timestampOffset;
    this.origAlpha = colors.cursorAlpha;
    this.iterator = iterator;
    this.midiStream = midiStream;
    this.tempo = midiStream.timeKeeper.getBpm();
    this.scene = scene;
    this.onLastSlice = onLastSlice;
    this.onFinishPlaying = onFinishPlaying;
    this.onStartPlayingMeasure = onStartPlayingMeasure;
    this.onStartPlayingMeasureContext = onStartPlayingMeasureContext;
    this.finishCbScope = cbScope;
    // The code for this cursor will only be used in a "Lesson" context
    // and will therefore not have an "Exercise" associated with it.
    // Hence, we handle all pause / resume related events directly in
    // the context of the cursor itself as it is the only scope where
    // gameplay related logic is implemented within the parent phaser
    // scene.
    this.setX(x - width / 2);
    // Gameplay load-hide for non-first phrases: zero the cursor's OWN alpha so
    // it doesn't flash at (0, phraseY) before its turn. `enable()` restores it
    // when the phrase activates. This is independent of the cursor-toggle mask.
    if (hidden) this.setAlpha(0);
    console.debug('[CursorMobile] constructor', { x, y, width, height, hidden });
    this.graphics.depth = 100;
  }

  /**
   * Mirror the global "Toggle cursor" state onto this cursor. This is the only
   * thing that flips the toggle mask; gameplay hiding goes through the cursor's
   * own alpha (enable/disable/blink/fade) and is untouched here. The own alpha
   * is left intact — only the mask flips — so toggling back on restores exactly
   * the alpha the cursor's gameplay state dictates.
   */
  setCursorVisibility(visible: boolean): void {
    this.cursorVisible = visible;
    if (!visible) this.blinkTween?.stop();
    this.draw();
  }

  /**
   * Final alpha committed at draw time: the cursor's own computed alpha masked
   * by the global toggle. Multiplicative, so the toggle can only pull alpha down
   * to 0 — it never reveals a cursor whose own alpha is already 0 (e.g. an
   * inactive/disabled phrase's cursor).
   */
  protected getRenderAlpha(): number {
    return this.cursorVisible ? this.alpha : 0;
  }

  disable() {
    console.debug('[CursorMobile] disable', this.idx);
    this.running = false;
    this.enabled = false;
    this.setAlpha(0);
  }

  enable(opaque = true) {
    // Set the cursor's own alpha regardless of the render mask: a cursor
    // enabled while hidden must come back at full opacity when un-hidden. The
    // mask in getRenderAlpha() keeps it invisible until then.
    if (opaque) this.setAlpha(colors.cursorAlpha);
    this.enabled = true;
  }

  isFirstPhrase() {
    return this.firstPhrase;
  }

  /**
   * True when the extensible iterator is still on the first vertical slice of the first phrase
   * (IteratorExtensible phraseIndex 0, indx 0). Used to keep the grand-staff brace in view on mobile.
   */
  isAtLessonIteratorStart(): boolean {
    const it = this.getEffectiveIterator();
    if (!('phraseIndex' in it)) return false;
    const ext = it as IteratorExtensible;
    return ext.phraseIndex === 0 && ext.indx === 0;
  }

  /** Index of the phrase the cursor is currently playing within its extensible
   *  iterator. Advances as the shared mobile cursor crosses phrase boundaries,
   *  so the manager can dedupe per-phrase prefetch requests. */
  getEffectivePhraseIndex(): number {
    const it = this.getEffectiveIterator();
    return 'phraseIndex' in it ? (it as IteratorExtensible).phraseIndex : 0;
  }

  /** Horizontal center in scene/world space (`moveTo` keeps `x` as the left edge). */
  getWorldCenterX(): number {
    return this.x - this.width / 2;
  }

  setEligible(eligible: boolean) {
    if (this.firstPhrase || eligible === this.eligible) return;
    this.eligible = eligible;
    NotifyReactEvents.SetPhraseEligible({ eligible });
  }

  getLastPlayedMeasure(): number {
    /*const timeStamp = this.getTimestamp();
    const measureTimestamp = this.iterator.measureTimestamps[this.iterator.CurrentMeasureIndex] + (this.timestampOffset ?? 0);
    let lastMeasure;
    if (timeStamp < measureTimestamp) {
      console.debug('midway', timeStamp);
      lastMeasure = this.startingMeasure + this.iterator.CurrentMeasureIndex - 2;
    }
    else {
      lastMeasure = this.startingMeasure + this.iterator.CurrentMeasureIndex - 1;
    }
    if (this.lastPlayedMeasure === undefined || this.lastPlayedMeasure < lastMeasure) this.lastPlayedMeasure = lastMeasure;
    return this.lastPlayedMeasure;*/
    const lastMeasure = this.startingMeasure + this.iterator.lastPlayedMeasure;
    console.debug('[pause] getLastPlayedMeasure', lastMeasure);
    if (this.lastPlayedMeasure === undefined || lastMeasure > this.lastPlayedMeasure) {
      this.lastPlayedMeasure = lastMeasure;
    }
    return this.lastPlayedMeasure;
    //return this.startingMeasure + this.iterator.CurrentMeasureIndex - 1;
  }

  setLastNotePos(x: number) {
    console.debug('[CursorMobile] setLastNotePos', x);
    this.lastNotePos = x;
  }

  /** True when demo override is active (app tour playback). */
  isDemoMode(): boolean {
    return this.demoTimeKeeper !== null && this.demoIterator !== null;
  }

  /**
   * Use a separate timekeeper and iterator for demo (app tour playback).
   * Saves current cursor state for later restore via clearDemoOverride().
   */
  setDemoOverride(timeKeeper: ITimeKeeper, iterator: ICursorIterator): void {
    this.savePosition();
    this.savedDemoState = {
      iterator: this.iterator,
      lastNotePos: this.lastNotePos,
      lastExpectedOnset: this.lastExpectedOnset,
      running: this.running,
      timestampOffset: this.timestampOffset,
      relPos: this.relPos,
    };
    this.demoTimeKeeper = timeKeeper;
    this.demoIterator = iterator;
    this.timestampOffset = 0;
    iterator.timestampOffset = 0;
    this.lastExpectedOnset = iterator.getNextTimestamp();
    this.clearBlink();
  }

  /**
   * Set cursor position for demo start so movement and metronome stay in sync.
   * Call after setDemoOverride(); uses first note position (center x).
   */
  setDemoStartPosition(firstNoteCenterX: number): void {
    if (!this.isDemoMode()) return;
    this.lastNotePos = firstNoteCenterX;
    this.moveTo(firstNoteCenterX);
  }

  targetNoteSmooth(targetX: number): void {
    this.movementTweenHandle?.stop();
    this.movementTweenHandle = this.scene.tweens.add({
      targets: this,
      x: targetX - this.width / 2,
      duration: 300,
      ease: 'Power2.Out',
      onUpdate: () => {
        this.draw();
      },
      callbackScope: this,
    });
  }

  savePosition() {
    this.savedPosition = this.x;
  }

  restorePosition() {
    this.movementTweenHandle?.stop();
    if (this.savedPosition != undefined) this.moveTo(this.savedPosition);
    this.savedPosition = undefined;
  }

  /**
   * Restore cursor to state before setDemoOverride(). Call when demo playback ends.
   */
  clearDemoOverride(): void {
    this.movementTweenHandle?.stop();
    if (this.savedPosition != undefined) this.moveTo(this.savedPosition ?? 0, true);
    this.savedPosition = undefined;
    if (this.savedDemoState === null) {
      this.demoTimeKeeper = null;
      this.demoIterator = null;
      return;
    }
    this.iterator = this.savedDemoState.iterator;
    this.lastNotePos = this.savedDemoState.lastNotePos;
    this.lastExpectedOnset = this.savedDemoState.lastExpectedOnset;
    this.running = this.savedDemoState.running;
    this.timestampOffset = this.savedDemoState.timestampOffset;
    this.relPos = this.savedDemoState.relPos;
    this.demoTimeKeeper = null;
    this.demoIterator = null;
    this.savedDemoState = null;
  }

  private getEffectiveTimeKeeper(): ITimeKeeper {
    return this.demoTimeKeeper ?? this.midiStream.timeKeeper;
  }

  private getEffectiveIterator(): ICursorIterator {
    return this.demoIterator ?? this.iterator;
  }

  startCountingIn(startTimestamp: number, lastOnset: number, duration: number) {
    this.enabled = true;
    this.lastNotePos = this.x;
    this.countInDuration = duration;
    this.lastExpectedOnset = -lastOnset;
  }

  getTimestamp() {
    return this.getEffectiveTimeKeeper().audioTimeToMeasureTimestamp();
  }

  calcPos() {
    const it = this.getEffectiveIterator();
    const nextXPosition = it.left(true);
    const nextTimestamp = it.getNextTimestamp();
    const timeDiff = nextTimestamp - this.lastExpectedOnset;
    const currentTimestamp =
      this.getEffectiveTimeKeeper().audioTimeToMeasureTimestamp() - (this.timestampOffset ?? 0);
    let prog = currentTimestamp - this.lastExpectedOnset;
    if (this.countInDuration) {
      prog = currentTimestamp + this.countInDuration;
    }
    let t = timeDiff ? prog / timeDiff : 1;
    if (this.countInDuration) t = clamp(Math.pow(t, 4), 0, 1);
    const x = lerp(this.lastNotePos, nextXPosition, t);
    /*(this.scene as SightReadingPhaser).debugLog(
      `[CursorMobile] calcPos x=${x} 
      lastNotePos=${this.lastNotePos} 
      nextXPosition=${nextXPosition} t=${t}
      nextTimestamp=${nextTimestamp}
      lastExpectedOnset=${this.lastExpectedOnset}`,
    );*/
    return x;
  }

  update() {
    const tk = this.getEffectiveTimeKeeper();
    const currentTimestamp = tk.audioTimeToMeasureTimestamp() - (this.timestampOffset ?? 0);
    /*(this.scene as SightReadingPhaser).debugLog(
      `[CursorMobile] update relPos=${this.relPos} idx=${this.idx} timestamp=${currentTimestamp}`,
    );*/
    if (!this.enabled) return;
    //console.debug('[CursorMobile] update', this.idx);
    const it = this.getEffectiveIterator();
    if (this.countInDuration !== undefined) {
      console.debug('[CursorMobile] update count in duration');
      this.relPos = this.calcPos();
      this.moveTo(this.relPos);
      let amt = clamp((currentTimestamp - this.lastExpectedOnset) / this.countInDuration, 0, 1);
      if (this.countInDuration) amt = Math.pow(amt, 4);
      const alpha = lerp(0, this.origAlpha, amt);
      if (amt === 1) this.countInDuration = undefined;
      this.setAlpha(alpha);
    }
    if (this.running) {
      it.checkPhraseCompletion(
        currentTimestamp,
        this.eligible,
        this.onFinishPlaying,
        this.finishCbScope,
        this.idx,
      );
    }
    const reachedEnd =
      this.running && currentTimestamp >= it.totalDuration() && !this.waitForTimerReset;
    if (reachedEnd) {
      this.running = false;
    }
    if (
      !this.running ||
      tk.isPaused() ||
      tk.audioTimeToMeasureTimestamp() >= it.totalDuration() + it.timestampOffset
    ) {
      return;
    }
    const bpm = tk.getBpm();
    if (this.tempo !== bpm) {
      this.tempo = bpm;
    }
    this.relPos = this.calcPos();
    this.flag = false;
    this.moveTo(this.relPos);
    if (it.EndReached) {
      //console.debug('[CursorMobile] end reached', this.idx);
      const lastNoteDuration = it.lastNoteDuration();
      const amt = (it.totalDuration() - currentTimestamp) / lastNoteDuration;
      const alpha = lerp(this.origAlpha, 0, Math.pow(1 - amt, 4));
      this.setAlpha(alpha);
    }
    const comp = it.EndReached ? it.totalDuration() : it.currentTimeStamp;
    if (this.running && currentTimestamp >= comp) {
      this.lastExpectedOnset = it.currentTimeStamp;
      this.lastNotePos = this.relPos;
      const onLastSliceCb = this.isDemoMode() ? undefined : this.onLastSlice;
      it.next(
        tk.getBpm(),
        onLastSliceCb,
        this.finishCbScope,
        this.onStartPlayingMeasure,
        this.onStartPlayingMeasureContext,
      );
    }
  }

  moveTo(pos: number, centreOrigin: boolean = false) {
    this.setX(centreOrigin ? pos : pos - this.width / 2);
  }

  reset(iterator: ICursorIterator, timestampOffset: number, measureOffset: number) {
    //console.log(
    ////`[CursorMobile.reset] idx=${this.idx} timestampOffset=${timestampOffset} measureOffset=${measureOffset} iteratorCursorDataLen=${iterator.cursorData.length}`,
    //);
    this.startingMeasure = measureOffset;
    this.iterator = iterator;
    //this.clearBlink();
    this.running = false;
    this.lastNotePos = 0;
    this.lastExpectedOnset = -1;
    this.relPos = 0;
    this.timestampOffset = timestampOffset;
    this.iterator.timestampOffset = timestampOffset;
  }

  addPhrase(
    cursorData: PrerenderedPhraseData[],
    id: string,
    timeSig: TimeSignature,
    phraseIndex: number,
  ) {
    this.iterator.addPhrase(cursorData, timeSig, id, phraseIndex);
  }

  unpause(_inaccComp?: number) {
    console.debug(
      '[CursorMobile.unpause] called — running was:',
      this.running,
      'isPaused:',
      this.midiStream.timeKeeper.isPaused(),
      'idx:',
      this.idx,
      this.lastNotePos,
    );
    this._pausedReturnCount = 0;
    this.waitForTimerReset = true;
    this.timer?.remove();
    this.timer = this.scene.time.addEvent({
      callback: () => {
        this.waitForTimerReset = false;
      },
      delay: 50,
      callbackScope: this,
    });
    this.clearBlink();
    this.calcPos();
    this.running = true;
    this.countInDuration = undefined;
  }

  pause() {
    this.running = false;
    this.timer?.remove();
    this.timer = undefined;
    this.clearBlink();
  }

  setStartPosition(start: number) {
    // `start` is the note's anchor x (the same coordinate `it.left()` and
    // `lastNotePos` use elsewhere) — i.e. the cursor's *center*. `moveTo`
    // applies the -width/2 offset so the cursor renders centered on `start`,
    // matching what `calcPos`/`moveTo` will do once playback starts. Otherwise
    // the cursor jumps half a width when the first `update()` runs.
    this.lastNotePos = start;
    this.moveTo(start);
  }

  setStart(lastNote: number, timestamp: number, measureOffset?: number) {
    const rollbackMeasure = measureOffset ?? 0;
    this.iterator.rollbackToMeasure(rollbackMeasure);
    const rollbackTimestamp = this.iterator.measureTimestamps[rollbackMeasure];
    this.lastNotePos = lastNote;
    // Use iterator-native measure timestamps after rollback so pause/resume
    // stays aligned even when phrases share one cursor iterator (mobile path).
    this.lastExpectedOnset = rollbackTimestamp ?? timestamp;
    this.moveTo(lastNote);
    console.debug('[CursorMobile] setStart', {
      lastNote,
      timestamp,
      measureOffset: rollbackMeasure,
      rollbackTimestamp,
    });
  }

  blink(duration: number) {
    if (!this.enabled || !this.cursorVisible) return;
    this.clearBlink();
    this.blinkTween = this.scene.tweens.add({
      targets: this,
      alpha: 0,
      duration,
      onUpdate: this.draw,
      callbackScope: this,
    });
  }

  clearBlink(alpha: number = colors.cursorAlpha) {
    this.blinkTween?.stop();
    this.setAlpha(alpha);
  }

  destroy(removeListeners?: boolean) {
    super.destroy();
    if (removeListeners) {
      RemoveListener('PauseExercise');
      RemoveListener('ResumeExercise');
    }
  }

  tryStart() {}

  waitForInput(tempo: number) {
    this.midiStream.timeKeeper.unpause(tempo, 0, 0.1, NoteType._32nd);
    //this.running = true;
  }
}
